import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Bell, LogOut, User, Info } from "lucide-react";
import { api } from "../api";
import { useAuth, useGuard } from "../auth";
import { onEvent } from "../socket";
import { Avatar } from "./cards";
import Logo from "@/components/landing/Logo";

const TopBar = () => {
  const { user, logout } = useAuth();
  const guard = useGuard();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications/unread-count").then((r) => setUnread(r.count ?? r.unread ?? 0)).catch(() => {});
    const off = onEvent("notification", () => setUnread((n) => n + 1));
    return off;
  }, [user]);

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const debounceRef = useRef(null);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/app/search?q=${encodeURIComponent(q.trim())}`);
  };

  const onQueryChange = (e) => {
    const val = e.target.value;
    setQ(val);
    // search-as-you-type: update the results page as the user types (debounced).
    // replace history so Back doesn't step through each keystroke.
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val.trim()) navigate(`/app/search?q=${encodeURIComponent(val.trim())}`, { replace: true });
    }, 350);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <header className="fixed inset-x-0 top-0 z-30 flex h-16 items-center gap-3 border-b border-white/10 bg-obsidian/90 px-4 backdrop-blur-xl lg:left-20 sm:gap-5 sm:px-6" data-testid="web-topbar">
      <Link to="/app" className="flex shrink-0 items-center gap-2.5" data-testid="topbar-brand">
        <Logo size={30} />
        <span className="hidden font-heading text-base font-bold tracking-tight text-white md:block">Michael C Streamline</span>
      </Link>
      <form onSubmit={onSearch} className="mx-auto flex h-11 w-full max-w-2xl items-center gap-3 rounded-full border border-white/10 bg-white/[0.05] px-5 transition-colors focus-within:border-white/30">
        <Search size={17} className="shrink-0 text-slate-500" />
        <input
          value={q}
          onChange={onQueryChange}
          placeholder="Search"
          data-testid="topbar-search-input"
          className="w-full bg-transparent text-sm text-white placeholder-slate-500 outline-none"
        />
      </form>
      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={() => (user ? navigate("/app/notifications") : guard())}
          className="relative rounded-full p-2.5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          data-testid="topbar-notifications-button"
        >
          <Bell size={19} />
          {user && unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-crimson px-1 font-mono text-[9px] font-bold text-white" data-testid="topbar-unread-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
        {user ? (
          <div className="relative" ref={menuRef}>
            <button onClick={() => setMenuOpen((o) => !o)} className="flex items-center" data-testid="topbar-avatar-button">
              <Avatar user={user} size={32} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-white/10 bg-surface shadow-2xl" data-testid="topbar-user-menu">
                <div className="border-b border-white/10 px-4 py-3">
                  <p className="truncate font-heading text-sm font-semibold text-white">{user?.displayName || user?.username}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <Link to="/app/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5" data-testid="menu-my-profile">
                  <User size={15} /> My Profile
                </Link>
                <Link to="/about" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5" data-testid="menu-about-app">
                  <Info size={15} /> About the app
                </Link>
                <button onClick={logout} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-crimson hover:bg-white/5" data-testid="menu-sign-out">
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate("/app/login")}
            data-testid="topbar-signin-button"
            className="rounded-full bg-crimson px-5 py-2 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover"
          >
            Sign in
          </button>
        )}
      </div>
    </header>
  );
};

export default TopBar;
